
import React from 'react';
import { ArrowDown, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { usePlatformDetection } from '@/utils/platform-utils';
import { cn } from '@/lib/utils';

interface PulseHeaderProps {
  title?: string;
  centerContent?: React.ReactNode;
  showBack?: boolean;
  rightElement?: React.ReactNode;
  onBackClick?: () => void;
  backToStory?: boolean;
  storyId?: number;
  className?: string;
}

const PulseHeader: React.FC<PulseHeaderProps> = ({ 
  title, 
  centerContent,
  showBack = true, 
  rightElement,
  onBackClick,
  backToStory = false,
  storyId,
  className
}) => {
  const navigate = useNavigate();
  const { isChapterScreen } = useTheme();
  const { platformType } = usePlatformDetection();

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else if (backToStory && storyId) {
      // First add the animation class to the body
      document.body.classList.add('animate-swipe-down');
      
      // Wait for animation to complete before navigating
      setTimeout(() => {
        document.body.classList.remove('animate-swipe-down');
        navigate(`/ip/${storyId}#livro`);
      }, 300);
    } else if (backToStory) {
      // If no story ID is provided, just go back
      document.body.classList.add('animate-swipe-down');
      
      setTimeout(() => {
        document.body.classList.remove('animate-swipe-down');
        navigate(-1);
      }, 300);
    } else {
      // Regular back navigation (for Story screen or other screens)
      navigate('/home');
    }
  };

  // Safe area class based on platform
  const getSafeAreaClass = () => {
    if (platformType === 'ios') {
      return 'header-safe'; // iOS will use native env() via CSS
    } else {
      return 'header-safe'; // Android/Web will use custom variables
    }
  };

  // Don't apply any theme-based classes on chapter screens, but include safe area support
  const headerClass = isChapterScreen 
    ? `flex items-center justify-between py-4 px-4 ${getSafeAreaClass()}`
    : `flex items-center justify-between py-4 px-4 text-foreground ${getSafeAreaClass()}`;

  // Merge the custom className with the default headerClass using cn utility
  const combinedClassName = cn(headerClass, className);

  return (
    <div className={getSafeAreaClass()}>
      <div className={combinedClassName}>
        <div className="flex items-center">
          {showBack && (
            <button 
              onClick={handleBackClick} 
              className={isChapterScreen ? "mr-4" : "mr-4 text-foreground"}
            >
              {backToStory ? (
                <ArrowDown className="w-6 h-6" />
              ) : (
                <ArrowLeft className="w-6 h-6" />
              )}
            </button>
          )}
          {centerContent || <h1 className="text-xl font-semibold">{title}</h1>}
        </div>
        {rightElement && (
          <div>
            {rightElement}
          </div>
        )}
      </div>
    </div>
  );
};

export default PulseHeader;
